import React, { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { 
  signInWithPopup,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase';
import TermsAndConditions from './TermsAndConditions';
import './LoginModal.css';

const LoginModal = ({ show, onHide, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      if (isSignUp && !agreedToTerms) {
        setError('You must agree to the Terms and Conditions to create an account.');
        document.getElementById('terms-checkbox-container').classList.add('terms-highlight');
        setTimeout(() => {
          document.getElementById('terms-checkbox-container')?.classList.remove('terms-highlight');
        }, 2000);
        return;
      }
      
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      onLoginSuccess?.();
      onHide();
    } catch (error) {
      console.error("Error signing in with Google:", error);
      setError(error.message);
    }
  };

  const handleEmailChange = async (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setError('');
    setIsGoogleAccount(false);
    setIsCheckingEmail(false);

    if (!newEmail || !newEmail.includes('@')) return;

    setIsCheckingEmail(true);
    try {
      await signInWithEmailAndPassword(auth, newEmail, 'dummy-password');
    } catch (error) {
      if (error.code !== 'auth/wrong-password') {
        try {
          await signInWithEmailAndPassword(auth, newEmail, 'another-dummy');
        } catch (secondError) {
          if (secondError.code === 'auth/user-not-found') {
            setIsGoogleAccount(false);
          } else if (
            secondError.code === 'auth/invalid-credential' || 
            secondError.code === 'auth/invalid-login-credentials'
          ) {
            setIsGoogleAccount(true);
            setError('This email is associated with a Google account. Please use "Sign in with Google" instead.');
          }
        }
      }
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (isGoogleAccount) {
      setError('This email is associated with a Google account. Please use "Sign in with Google" instead.');
      return;
    }

    if (isSignUp && !agreedToTerms) {
      setError('You must agree to the Terms and Conditions to create an account.');
      document.getElementById('terms-checkbox-container').classList.add('terms-highlight');
      setTimeout(() => {
        document.getElementById('terms-checkbox-container')?.classList.remove('terms-highlight');
      }, 2000);
      return;
    }

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      onLoginSuccess?.();
      onHide();
    } catch (error) {
      console.error("Authentication error:", error);
      
      if (error.code === 'auth/invalid-credential' || 
          error.code === 'auth/invalid-login-credentials') {
        setError('This email is associated with a Google account. Please use "Sign in with Google" instead.');
        setIsGoogleAccount(true);
        return;
      }

      switch (error.code) {
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email. Please sign up first.');
          setIsSignUp(true);
          break;
        case 'auth/email-already-in-use':
          setError('An account already exists with this email. Please try logging in instead.');
          setIsSignUp(false);
          break;
        case 'auth/weak-password':
          setError('Password should be at least 6 characters long.');
          break;
        default:
          setError(error.message);
      }
    }
  };

  return (
    <>
      <Modal 
        show={show} 
        onHide={onHide} 
        centered
        dialogClassName="compact-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">
            {isSignUp ? 'Sign Up' : 'Log In'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isSignUp && (
            <div className="terms-notice mb-3">
              <Alert variant="info" className="py-2">
                You must agree to our Terms and Conditions to create an account.
              </Alert>
            </div>
          )}
          
          <button 
            className="google-sign-in-button"
            onClick={handleGoogleSignIn}
            disabled={isSignUp && !agreedToTerms}
          >
            <img 
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="google-icon"
            />
            Sign in with Google
          </button>

          <Form onSubmit={handleEmailSignIn}>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={handleEmailChange}
                isInvalid={isGoogleAccount}
                disabled={isCheckingEmail}
              />
              {isGoogleAccount && (
                <Form.Control.Feedback type="invalid">
                  This email is associated with a Google account. Please use "Sign in with Google" instead.
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isGoogleAccount || isCheckingEmail}
              />
            </Form.Group>

            {isSignUp && (
              <Form.Group 
                className="mb-3" 
                id="terms-checkbox-container"
              >
                <div className="terms-checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    className="form-check-input"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    aria-invalid={isSignUp && error === 'You must agree to the Terms and Conditions to create an account.' ? 'true' : 'false'}
                  />
                  <label htmlFor="terms-checkbox" className="terms-label">
                    I agree to the <button type="button" className="link-button" onClick={(e) => {
                      e.preventDefault();
                      setShowTerms(true);
                    }}>Terms of Use, Privacy Policy and other Legal Information</button>
                  </label>
                </div>
                {isSignUp && error === 'You must agree to the Terms and Conditions to create an account.' && (
                  <div className="terms-error-message">
                    Please check this box to continue
                  </div>
                )}
              </Form.Group>
            )}

            {error && !isGoogleAccount && error !== 'You must agree to the Terms and Conditions to create an account.' && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="login-button"
            >
              {isSignUp ? 'Sign Up' : 'Log In'}
            </button>
          </Form>

          <div className="text-center mt-3">
            <button 
              className="signup-link"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
              }}
            >
              {isSignUp ? 'Already have an account? Log In' : "Don't have an account? Sign Up"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
      
      <TermsAndConditions 
        show={showTerms} 
        onHide={() => setShowTerms(false)}
      />
    </>
  );
};

export default LoginModal;